/**
 * Form helper utilities — mirrors Flutter CandidateFormBloc form parsing logic.
 *
 * Converts raw JSON form configuration from the API into usable state structures,
 * handles field value extraction, experience calculation, and validation.
 *
 * Flutter source:
 *   lib/application/candidate_form/candidate_form_bloc.dart
 *   lib/infrastructure/models/candidate_application/candidate_application_modal.dart
 */
import type {
  FldListJson,
  FldListState,
  ComponentState,
  CandidateApplicationJson,
  OptionData,
} from '@/types/application.types';
import { FieldType } from '@/types/application.types';
import { useLocalizationStore } from '@/store/localizationStore';

/* ────────────────────────────── Parse field JSON → state ────────────────────────────── */

/**
 * Parse a single field JSON to state — mirrors Flutter FldList.fromJson().
 */
export function parseFldJson(json: FldListJson): FldListState {
  let dropDownOptions: OptionData[] = [];

  // Experience dropdown options — mirrors Flutter FldList.fromJson() EXP handling
  if (json.subType === 'EXP' || json.type === 'EXPERIENCE_DROP_DOWN') {
    if (json.key?.includes('experienceInYears')) {
      dropDownOptions = Array.from({ length: 41 }, (_, i) => ({
        key: `${i} ${i > 1 || i === 0 ? 'years' : 'year'}`,
        displayKey: `${i} ${i > 1 || i === 0 ? 'years' : 'year'}`,
      }));
    } else if (json.key?.includes('experienceInMonths')) {
      dropDownOptions = Array.from({ length: 12 }, (_, i) => ({
        key: `${i} ${i > 1 || i === 0 ? 'months' : 'month'}`,
        displayKey: `${i} ${i > 1 || i === 0 ? 'months' : 'month'}`,
      }));
    }
  } else if (json.dropDownOptions && Array.isArray(json.dropDownOptions)) {
    dropDownOptions = json.dropDownOptions.map((opt) => {
      if (typeof opt === 'object' && opt !== null) {
        return { key: String(opt.key ?? ''), displayKey: String(opt.displayKey ?? '') };
      }
      return { key: String(opt), displayKey: String(opt) };
    });
  }

  return {
    key: json.key ?? '',
    title: json.title ?? '',
    subTitle: json.subTitle ?? '',
    hintText: json.hintText ?? '',
    isMandatory: json.isMandatory ?? false,
    isMandatoryForLocal: json.isMandatory ?? false,
    type: json.type ?? '',
    value: json.value ?? '',
    displayValue: '',
    disableByKey: json.disableByKey ?? '',
    isDisabled: json.isDisabled ?? false,
    isMobileFld: json.isMobileFld ?? false,
    isEmailFld: json.isEmailFld ?? false,
    digitsOnly: json.digitsOnly ?? false,
    masterDataKey: json.masterDataKey ?? '',
    dropDownOptions,
    isCurrentCompany: json.isCurrentCompany ?? false,
    disableCurrentCompany: json.disableCurrentCompany ?? false,
    enableSearch: json.enableSearch ?? true,
    isCurrencyDropDown: json.isCurrencyDropDown ?? false,
    currencySymbol: json.currencySymbol ?? '₹',
    currencyCode: json.currencyCode ?? 'INR',
    isInValid: json.isInValid ?? false,
    errorText: json.errorText ?? '',
    subType: json.subType ?? '',
    countryCode: json.countryCode ?? '91',
    data: json.data,
  };
}

/**
 * Parse a component JSON to state — mirrors Flutter Component.fromJson().
 */
export function parseComponentJson(json: Record<string, unknown> | undefined): ComponentState {
  if (!json) return { headerTitle: '', isHighestQualification: false, fields: [] };
  return {
    headerTitle: (json.headerTitle as string) ?? '',
    isHighestQualification: (json.isHighestDegree as boolean) ?? false,
    fields: Array.isArray(json.fieldList)
      ? (json.fieldList as FldListJson[]).map(parseFldJson)
      : [],
  };
}

/**
 * Parse the full application form JSON to structured state.
 * Mirrors Flutter CandidateApplication.fromJson().
 */
export function parseApplicationFormJson(json: CandidateApplicationJson | null) {
  if (!json) return null;
  return {
    personalInformation: parseComponentJson(
      json.personalInformation as unknown as Record<string, unknown>,
    ),
    address: parseComponentJson(json.address as unknown as Record<string, unknown>),
    education: Array.isArray(json.education)
      ? json.education.map((e) => parseComponentJson(e as unknown as Record<string, unknown>))
      : [],
    experience: Array.isArray(json.experience)
      ? json.experience.map((e) => parseComponentJson(e as unknown as Record<string, unknown>))
      : [],
    professionalDetails: parseComponentJson(
      json.professionalDetails as unknown as Record<string, unknown>,
    ),
    professionalDetailsNonIndia: parseComponentJson(
      json.professionalDetailsNonIndia as unknown as Record<string, unknown>,
    ),
    additionalInfo: parseComponentJson(
      json.additionalInfo as unknown as Record<string, unknown>,
    ),
    questionInfo: parseComponentJson(json.questionInfo as unknown as Record<string, unknown>),
  };
}

export type ParsedApplication = NonNullable<ReturnType<typeof parseApplicationFormJson>>;

/* ────────────────────────────── Experience helpers ────────────────────────────── */

/**
 * Mirrors Flutter GenericHelpers.getMonthsToYearArray().
 * Converts total months into [years string, months string].
 */
export function getMonthsToYearArray(experienceInMonths: number): [string, string] {
  const years = Math.floor(experienceInMonths / 12);
  const months = experienceInMonths % 12;
  return [
    `${years} ${years > 1 || years === 0 ? 'years' : 'year'}`,
    `${months} ${months > 1 || months === 0 ? 'months' : 'month'}`,
  ];
}

/**
 * Calculate total experience in months from years + months strings.
 * Mirrors Flutter candidate_form_bloc.dart experience calculation.
 */
export function calculateExperienceInMonths(
  yearsStr: string,
  monthsStr: string,
): number {
  const years = parseInt(yearsStr.replace(/[^0-9]/g, '') || '0', 10);
  const months = parseInt(monthsStr.replace(/[^0-9]/g, '') || '0', 10);
  return years * 12 + months;
}

/* ────────────────────────────── Field value extraction ────────────────────────────── */

/**
 * Check if a field is a text-input type.
 */
export function isTextInputField(field: FldListState): boolean {
  return (
    field.type === FieldType.TEXT_FLD ||
    field.type === FieldType.SEARCH_TEXT_FLD
  );
}

/**
 * Check if a field is a date type.
 */
export function isDateField(field: FldListState): boolean {
  return field.type === FieldType.DATE;
}

/* ────────────────────────────── Validation (Flutter BuildFormField validators) ────────────────────────────── */

type Translate = (key: string) => string;
const identity: Translate = (key) => key;

export interface ValidationContext {
  /**
   * Other fields of the same education / experience entry — the start/end date check
   * (Flutter listOfComp[topIndex]). Leave it out elsewhere: Flutter passes listOfComp only there.
   */
  siblings?: FldListState[];
}

/** Flutter FormValidators.isValidEmail regex */
const EMAIL_REGEX =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

/** Keys whose mandatory text only needs 1 character (Flutter validateText minLen). */
const MIN_LENGTH_ONE = ['noticePeriodInDays', 'firstName', 'lastName'];

/**
 * Flutter getDatePrimaryTextFld validator, non-mandatory branch: start date after end date (or the
 * reverse) of the same entry is an error. Flutter also clears the offending date (see
 * `isDateOrderError`); the caller does that.
 */
function dateOrderError(field: FldListState, siblings: FldListState[] | undefined, t: Translate): string | null {
  const value = (field.value ?? '').toString();
  if (!value || !siblings) return null;
  const current = Number(value);
  if (field.key === 'startDate') {
    const end = siblings.find((f) => f.key === 'endDate');
    if (end?.value && current > Number(end.value)) return t('start date should be lesser than end date');
  } else if (field.key === 'endDate') {
    const start = siblings.find((f) => f.key === 'startDate');
    if (start?.value && current < Number(start.value)) return t('end date should be greater than start date');
  }
  return null;
}

/**
 * A DATE field failing the start/end comparison. Flutter's validator clears such a date
 * (`field.value = ''`) as it reports the error, so the form must clear it too.
 */
export function isDateOrderError(field: FldListState, ctx: ValidationContext = {}): boolean {
  return field.type === FieldType.DATE && !field.isMandatory && dateOrderError(field, ctx.siblings, identity) != null;
}

/**
 * Validate one field with the rules of Flutter BuildFormField (form_util.dart) + FormValidators.
 * Returns the error text, or null when valid.
 *
 * The application forms never pass `primaryEmailId` to BuildFormField, so Flutter's
 * "Primary Email and Alternate Email cannot be same" check can't fire there and isn't done here.
 */
export function validateFormField(
  field: FldListState,
  t: Translate = identity,
  ctx: ValidationContext = {},
): string | null {
  const value = (field.value ?? '').toString();
  // FormValidators.validateText / RadioFormField: "<title> is required"
  const required = `${field.title} ${t('is required')}`;

  switch (field.type) {
    case FieldType.TEXT_FLD: {
      // Flutter validateMobile receives '+cc', which its digit-size table never matches → no check
      if (field.isMobileFld && value) return null;
      if (field.isEmailFld) {
        if (field.key === 'secondaryEmailId' && !value) return null;
        return EMAIL_REGEX.test(value) ? null : t('Not a valid email');
      }
      if (field.key === 'noticePeriodInDays' && value && Number(value) > 180) {
        return t('Days should be less than 180 days.');
      }
      if (field.isMandatory) {
        const minLen = MIN_LENGTH_ONE.includes(field.key) ? 1 : 2;
        if (!value) return required;
        if (value.length < minLen) {
          return `${field.title} ${t('must have at least')} ${minLen} ${t('characters')}`;
        }
      }
      return null;
    }

    case FieldType.DESCRIPTION_TEXT_FLD:
    case FieldType.SEARCH_TEXT_FLD:
    case FieldType.RADIO_BUTTON:
      return field.isMandatory && !value ? required : null;

    // SearchDropDown validator: the field's errorText, when set, names the field
    case FieldType.SEARCH_DROP_DOWN:
    case FieldType.EXPERIENCE_DROP_DOWN:
      return field.isMandatory && !value ? `${field.errorText || field.title} ${t('is required')}` : null;

    // CheckBoxFormField validator
    case FieldType.CHECKBOX:
      return field.isMandatory && value !== 'true' ? t('Required') : null;

    // A mandatory date is only checked for a value; the start/end comparison is for optional ones
    case FieldType.DATE:
      if (field.isMandatory) return value ? null : required;
      return dateOrderError(field, ctx.siblings, t);

    // DROPDOWN_FOR_SINGLE_SELECTION (CustomDropdownButtonFormField) has no validator; EMPTY is a
    // spacer and any other type renders nothing in Flutter
    default:
      return null;
  }
}

/** Single-field validation (used by Easy Apply). */
export function validateField(field: FldListState, value: string): string | null {
  return validateFormField({ ...field, value });
}

/**
 * Validate all fields in a component.
 * Returns a Map of fieldKey → error message.
 */
export function validateComponent(
  component: ComponentState,
  t: Translate = identity,
  ctx: ValidationContext = {},
): Map<string, string> {
  const errors = new Map<string, string>();
  for (const field of component.fields) {
    if (field.isDisabled) continue;
    const err = validateFormField(field, t, { ...ctx, siblings: component.fields });
    if (err) errors.set(field.key, err);
  }
  return errors;
}

/* ────────────────────────────── Populate form from API response ────────────────────────────── */

/**
 * Populate fields from API response data — one section of Flutter _PopulateData:
 *   - text / search-text: the value when present
 *   - date: epoch millis → value + formatted display
 *   - everything else: `key<Field>` → value with `<field>` as the display label, else `<field>`
 */
export function populateComponentFields(
  component: ComponentState,
  data: Record<string, unknown> | undefined,
): ComponentState {
  if (!data) return component;

  const updatedFields = component.fields.map((field) => {
    if (!field.key) return field;
    const rawValue = data[field.key];
    const keyValue = data[`key${field.key.charAt(0).toUpperCase()}${field.key.slice(1)}`];

    if (isTextInputField(field)) {
      return rawValue != null && rawValue !== '' ? { ...field, value: String(rawValue) } : field;
    }
    if (isDateField(field)) {
      if (typeof rawValue === 'number' && rawValue > 0) {
        return { ...field, value: String(rawValue), displayValue: formatDateForDisplay(new Date(rawValue)) };
      }
      return field;
    }
    if (keyValue != null && keyValue !== '') {
      return { ...field, value: String(keyValue), displayValue: rawValue != null ? String(rawValue) : '' };
    }
    return { ...field, value: rawValue != null ? String(rawValue) : '', displayValue: '' };
  });

  return { ...component, fields: updatedFields };
}

/**
 * Submitted value of a field, as Flutter's `fldItem.value ?? ''`: dates as epoch millis, a touched
 * checkbox as a boolean (an untouched one has no value, so `''`), and the
 * DROPDOWN_FOR_SINGLE_SELECTION choice as the int CustomDropdownButtonFormField stores.
 */
export function fieldPayloadValue(field: FldListState): unknown {
  const value = String(field.value ?? '');
  if (field.type === FieldType.DATE && /^\d+$/.test(value)) return Number(value);
  if (field.type === FieldType.CHECKBOX) return value === '' ? '' : value === 'true';
  if (field.type === FieldType.DROPDOWN_FOR_SINGLE_SELECTION && /^\d+$/.test(value)) return Number(value);
  return value;
}

/**
 * Flutter CandidateFormBloc.applyGenderMandatoryInCountries. A gender field that the form config
 * leaves optional becomes mandatory when the job's country is listed in the tenant preference
 * `genderMandatoryInCountries`.
 */
export function applyGenderMandatoryInCountries(
  form: ParsedApplication,
  jobCountry: string,
  preferences: unknown,
): void {
  const prefs = preferences as Record<string, unknown> | null;
  const countries = Array.isArray(prefs?.genderMandatoryInCountries)
    ? (prefs.genderMandatoryInCountries as unknown[]).map(String)
    : [];
  const field = form.personalInformation.fields.find((f) => f.key === 'genderCode');
  if (field && !field.isMandatoryForLocal) {
    field.isMandatory = countries.some((c) => c.trim().toLowerCase() === jobCountry.trim().toLowerCase());
  }
}

/**
 * Integrity questionnaire: a question with `disableByKey` is shown (and required) only while
 * the question it depends on is answered "yes" (Flutter WebIntegrityQuestionnaire).
 */
export function isQuestionVisible(field: FldListState, fields: FldListState[]): boolean {
  if (!field.disableByKey) return true;
  const controller = fields.find((f) => f.key === field.disableByKey || f.title === field.disableByKey);
  if (!controller || controller === field) return true;
  return (controller.value ?? '').toString().trim().toLowerCase() === 'yes';
}

const SHORT_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Abbreviated month name in `locale` (Dart `MMM`, the format-context form), English as fallback. */
function shortMonthName(date: Date, locale: string): string {
  try {
    const month = new Intl.DateTimeFormat(locale.replace(/_/g, '-'), { day: 'numeric', month: 'short', year: 'numeric' })
      .formatToParts(date)
      .find((part) => part.type === 'month')?.value;
    if (month) return month;
  } catch {
    // Unknown locale code
  }
  return SHORT_MONTHS[date.getMonth()];
}

/**
 * Format a Date object for display.
 * Mirrors Flutter DateFormat('dd MMM yyyy', LocalDataService.getAppLanguage()).format(date):
 * the month name follows the app language.
 */
export function formatDateForDisplay(
  date: Date,
  locale: string = useLocalizationStore.getState().currentLanguage || 'en',
): string {
  const day = String(date.getDate()).padStart(2, '0');
  return `${day} ${shortMonthName(date, locale)} ${date.getFullYear()}`;
}

/* ────────────────────────────── File validation ────────────────────────────── */

/**
 * Validate file name — mirrors Flutter RegExp r'^[^\/:*?"<>$|\x00-\x1F\x7F]+$'.
 */
export function isValidFileName(name: string): boolean {
  const regex = /^[^\\/:*?"<>$|\x00-\x1F\x7F]+$/;
  return regex.test(name);
}

/**
 * Get file extension from file name.
 */
export function getFileExtension(fileName: string): string {
  return fileName.split('.').pop()?.toLowerCase() ?? '';
}
